/**
 * Why a pack accept was refused, read from the route's answer. Import-free so it can be unit-tested
 * without the app's dependency graph (see the jest note in CLAUDE.md).
 *
 * The harness names each refusal with a stable `code` beside its English `error` sentence:
 * `own_pack`, `needs_reshare`, `disabled`, `not_available`, `invalid_params`, `no_metasite`
 * (`orion-harness-poc/src/endpoints/packs.ts`, `AcceptPackFailureCode`). Branch on the code, never on
 * the sentence — it is for humans and logs and may be reworded at any time.
 *
 * The screen used to keep two distinctions (401 sign-in, 410 gone) and fold every other failure into
 * "this link has expired", which told a sender who opened their own invite that the link had died and
 * sent the receiver of a torn pack to the wrong remedy. A code the client does not know still lands on
 * `invalid`, so an older client keeps working against a newer harness and the other way round.
 *
 * **`temporary` is a failure that never was the harness's answer.** The harness refuses only with a 400,
 * 404, 409 or 410, each with a code. A 403 with no code, a 5xx, a 499, a 408 or a 429 comes from the
 * gateway or the transport — on 7–8 Oct 2026 the gateway's CSRF check blocked every web accept for 22
 * hours (`NoHeader`, fixed by #11577) and each one read as "this link has expired", for a link that was
 * fine. Those now say "something went wrong, try again", and the screen offers the retry.
 */
export type PackAcceptFailure =
  | { kind: 'unauthenticated' }
  | { kind: 'gone' }
  | { kind: 'ownPack' }
  | { kind: 'needsReshare' }
  | { kind: 'temporary' }
  | { kind: 'invalid' };

/** The harness's refusal codes (`AcceptPackFailureCode`). Whether a code is present at all decides `temporary`, so
 *  `/api/pack/accept` passes on every one of them and nothing else. */
export const PACK_ACCEPT_FAILURE_CODES: readonly string[] = ['invalid_params', 'no_metasite', 'own_pack', 'not_available', 'disabled', 'needs_reshare'];

/** Statuses the harness never answers an accept with: the gateway's or the transport's, not a refusal. */
const TEMPORARY_STATUSES = new Set([403, 408, 429, 499]);

export function readPackAcceptFailure(status: number, body: unknown): PackAcceptFailure {
  if (status === 401) return { kind: 'unauthenticated' };
  const code = typeof body === 'object' && body !== null && typeof (body as { code?: unknown }).code === 'string' ? (body as { code: string }).code : undefined;
  if (code === 'own_pack') return { kind: 'ownPack' };
  if (code === 'needs_reshare') return { kind: 'needsReshare' };
  if (status === 410 || code === 'disabled') return { kind: 'gone' };
  if (code === undefined && (status >= 500 || TEMPORARY_STATUSES.has(status))) return { kind: 'temporary' };
  return { kind: 'invalid' };
}
